import { GAME_CONFIG, type GameConfig, type TaskAudienceConfig } from "../config/gameConfig";
import type {
  AchievementConfig,
  ArenaCatalogEntry,
  PremiumOfferConfig,
  TaskDefinition,
  UpgradeTrackConfig,
} from "../config/gameConfig";
import type {
  AchievementId,
  ArenaGameAvailability,
  ArenaGameId,
  DailyTaskCode,
  DiamondSkinId,
  GameState,
  PremiumOfferKind,
  SeasonTaskCode,
  TaskMetric,
  UpgradeCode,
} from "../domain/types";

type PublishedUpgradeRule = {
  code: UpgradeCode;
  effect: UpgradeTrackConfig["effect"];
  initialLevel: number;
  coreMaxLevel?: number;
  levels: { level: number; effectValue: string; costToReach: string | null }[];
};

type PublishedTaskRule = {
  code: string;
  metric: string;
  target: string;
  reward: string;
  contentKey?: string;
  compatibilityGroup?: string | null;
  audience?: TaskAudienceConfig | null;
  enabled: boolean;
  startsAt: string | null;
  endsAt: string | null;
  retroactive: boolean;
  claimPhase?: "immediate" | "season-end";
  chainId?: string | null;
  tier?: number | null;
  prerequisiteCode?: string | null;
};

type PublishedGameConfig = {
  seasonRulesVersionId: string;
  publicRules: {
    seasonRules: {
      algorithm: "DIAMOND_TAPPER_SEASON_RULES_V1";
      economy: {
        initialBalance: string;
        tapReward: string;
        passiveRatePeriodSeconds: number;
        offlineCapSeconds: number;
        maxTapBatchSize: number;
        maxAcceptedTapsPerSecond: number;
        maxTapBurstSize?: number;
      };
      energy: {
        costPerTap: number;
        dailyRefillLimit: number;
        refillPriceBasisPoints: number;
      };
      workshop: { visualStageUpgradeThresholds: [number, number, number, number] };
      upgrades: Record<UpgradeCode, PublishedUpgradeRule>;
      referrals: GameConfig["referrals"];
    };
    liveOpsCatalog: {
      algorithm: "DIAMOND_TAPPER_LIVEOPS_V2";
      daily: {
        assignmentCount: 3;
        forcedTaskCode: string | null;
        rewardBase: string;
        rewardDailyStep: string;
        rewardGrowthCapDay: number;
        tasks: PublishedTaskRule[];
        streakMilestones: { day: number; bonus: string }[];
      };
      seasonTasks: PublishedTaskRule[];
      achievements: {
        id: string;
        category: string;
        metric: string;
        target: string;
        comparison?: "at-least" | "at-most";
        finalSeasonOnly?: boolean;
      }[];
    };
    onboardingRoute:
      | {
          algorithm: "DIAMOND_TAPPER_ONBOARDING_V4";
          version: 4;
          tapTarget: number;
          passiveTarget: string;
        }
      | {
          algorithm: "DIAMOND_TAPPER_ONBOARDING_V5";
          version: 5;
          tapTarget: number;
          firstOutputReward: string;
        }
      | {
          algorithm: "DIAMOND_TAPPER_ONBOARDING_V6";
          version: 6;
          tapTarget: number;
          firstOutputReward: string;
        };
    rewardCampaign: {
      algorithm: "DIAMOND_TAPPER_REWARD_CAMPAIGN_V1";
      campaignCode: string;
      seasonCount: number;
      claimWindowSeconds: number;
      retention?: GameConfig["retention"];
    };
    contentCatalog: {
      algorithm: "DIAMOND_TAPPER_CONTENT_V1";
      version: string;
      seasonTitle: string;
      arenaCatalog: { id: string; availability: string; art: string }[];
    };
    arenaRules: {
      algorithm: "DIAMOND_TAPPER_CRYSTAL_PENALTY_V1";
      gameId: "crystal-penalty";
      stake: string;
      zoneCount: number;
      regulationSeries: number;
      maxSuddenDeathSeries: number;
      guardianFallbackSeconds: number;
      introMilliseconds: number;
      turnSeconds: number;
      revealMilliseconds: number;
      guardianThinkMinMilliseconds: number;
      guardianThinkMaxMilliseconds: number;
      guardianName: string;
      guardianInitialBalance: string;
    };
    premiumCatalog: {
      algorithm: "DIAMOND_TAPPER_PREMIUM_CATALOG_V1";
      starsReference?: {
        developerRewardUsdMicrosPerStar: string;
        withdrawalMinimumStars: number;
        checkedAt: string;
      };
      offers: {
        id: string;
        kind: string;
        priceStars: number;
        startsAt: string | null;
        endsAt: string | null;
        purchaseLimit: number | null;
        offlineReserveSeconds?: number;
        durationSeconds?: number;
        multiplierBasisPoints?: number;
        grantBasisPoints?: number;
        availableForSeasonSeconds?: number;
        collectibleId?: string;
        featured: boolean;
      }[];
    };
  };
};

const UPGRADE_CODES = new Set<UpgradeCode>([
  "precisionCutter",
  "mechanicalCutter",
  "energyCapacity",
  "energyRecovery",
]);
const TASK_METRICS = new Set<TaskMetric>([
  "confirmed-taps",
  "primary-produced",
  "passive-produced",
  "energy-spent",
  "offline-collections",
  "return-visits",
  "upgrade-purchases",
  "arena-matches",
  "arena-wins",
  "pvp-matches",
  "daily-routes-claimed",
  "active-invites",
  "confirmed-invites",
  "channel-membership",
  "chat-membership",
]);
const ACHIEVEMENT_IDS = new Set<AchievementId>([
  "first-spark",
  "living-facet",
  "master-rhythm",
  "light-architect",
  "makers-circle",
  "perfect-facet",
  "crystal-reserve",
  "silent-current",
  "arena-guardian",
  "season-one-participant",
  "season-two-participant",
  "streak-seven",
  "streak-fifteen",
  "streak-thirty",
  "chain-conqueror",
  "branch-master",
  "workshop-sovereign",
  "pvp-first-win",
  "pvp-ten-wins",
  "arena-ten-matches",
  "arena-fifty-matches",
  "active-friend-one",
  "active-friend-three",
  "active-friend-ten",
  "top-ten-percent",
  "top-hundred",
  "top-ten",
  "season-champion",
]);
const KNOWN_COLLECTIBLE_IDS = new Set<DiamondSkinId | "season-01-signet">([
  "skin-aurora-prism",
  "skin-black-diamond",
  "skin-solar-heart",
  "skin-emerald-abyss",
  "season-01-signet",
]);
const PREMIUM_KINDS = new Set<PremiumOfferKind>([
  "offline-reserve",
  "passive-boost",
  "tap-boost",
  "energy-recovery-boost",
  "tap-production-grant",
  "cosmetic",
  "season-signet",
]);
const ARENA_GAMES = new Set<ArenaGameId>(["crystal-penalty", "resonance-rush", "relic-duel"]);
const ARENA_AVAILABILITY = new Set<ArenaGameAvailability>(["playable", "coming-soon"]);

function record(value: unknown, name: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`Published game config is missing ${name}`);
  }
  return value as Record<string, unknown>;
}

function assertPublishedConfig(value: unknown): asserts value is PublishedGameConfig {
  const config = record(value, "config");
  if (typeof config.seasonRulesVersionId !== "string") throw new Error("Published game config version is invalid");
  const rules = record(config.publicRules, "publicRules");
  if (record(rules.seasonRules, "seasonRules").algorithm !== "DIAMOND_TAPPER_SEASON_RULES_V1") {
    throw new Error("Unsupported season rules");
  }
  if (record(rules.liveOpsCatalog, "liveOpsCatalog").algorithm !== "DIAMOND_TAPPER_LIVEOPS_V2") {
    throw new Error("Unsupported live operations catalog");
  }
  const onboardingAlgorithm = record(rules.onboardingRoute, "onboardingRoute").algorithm;
  if (
    onboardingAlgorithm !== "DIAMOND_TAPPER_ONBOARDING_V4" &&
    onboardingAlgorithm !== "DIAMOND_TAPPER_ONBOARDING_V5" &&
    onboardingAlgorithm !== "DIAMOND_TAPPER_ONBOARDING_V6"
  ) {
    throw new Error("Unsupported onboarding route");
  }
  if (record(rules.rewardCampaign, "rewardCampaign").algorithm !== "DIAMOND_TAPPER_REWARD_CAMPAIGN_V1") {
    throw new Error("Unsupported reward campaign");
  }
  if (record(rules.contentCatalog, "contentCatalog").algorithm !== "DIAMOND_TAPPER_CONTENT_V1") {
    throw new Error("Unsupported content catalog");
  }
  if (record(rules.arenaRules, "arenaRules").algorithm !== "DIAMOND_TAPPER_CRYSTAL_PENALTY_V1") {
    throw new Error("Unsupported Arena rules");
  }
  if (record(rules.premiumCatalog, "premiumCatalog").algorithm !== "DIAMOND_TAPPER_PREMIUM_CATALOG_V1") {
    throw new Error("Unsupported premium catalog");
  }
}

function upgradeRule(rule: PublishedUpgradeRule): UpgradeTrackConfig {
  if (!UPGRADE_CODES.has(rule.code)) throw new Error(`Unsupported upgrade code: ${rule.code}`);
  if (rule.levels.length === 0 || rule.levels.length > 100) {
    throw new Error(`Unsupported ${rule.code} level count`);
  }
  if (rule.coreMaxLevel !== undefined && (
    !Number.isSafeInteger(rule.coreMaxLevel) || rule.coreMaxLevel <= rule.initialLevel ||
    !rule.levels.some((level) => level.level === rule.coreMaxLevel)
  )) throw new Error(`Unsupported ${rule.code} core level`);
  const integerAmount = /^(0|[1-9]\d{0,39})$/;
  const levels = rule.levels.map((level, index) => {
    if (!Number.isSafeInteger(level.level) || level.level < 0 || level.level > 99) {
      throw new Error(`Unsupported ${rule.code} level: ${level.level}`);
    }
    const previous = rule.levels[index - 1];
    if (index > 0 && (!previous || level.level !== previous.level + 1)) {
      throw new Error(`Non-contiguous ${rule.code} levels`);
    }
    if (!integerAmount.test(level.effectValue)) throw new Error(`Unsupported ${rule.code} effect`);
    if (
      index === 0
        ? level.costToReach !== null
        : level.costToReach === null || !integerAmount.test(level.costToReach) || BigInt(level.costToReach) === 0n
    ) {
      throw new Error(`Unsupported ${rule.code} cost`);
    }
    return { ...level, level: level.level };
  });
  if (!Number.isSafeInteger(rule.initialLevel) || rule.initialLevel < 0 || rule.initialLevel > 99) {
    throw new Error(`Unsupported initial ${rule.code} level`);
  }
  if (levels[0]?.level !== rule.initialLevel) throw new Error(`Unsupported initial ${rule.code} level`);
  return { ...rule, initialLevel: rule.initialLevel, levels };
}

function taskRule<Code extends string>(
  rule: PublishedTaskRule,
): TaskDefinition<Code> {
  if (!/^[a-z0-9][a-z0-9-]{0,63}$/.test(rule.code)) throw new Error(`Unsupported task code: ${rule.code}`);
  if (!TASK_METRICS.has(rule.metric as TaskMetric)) throw new Error(`Unsupported task metric: ${rule.metric}`);
  const { prerequisiteCode, ...base } = rule;
  return {
    ...base,
    code: rule.code as Code,
    metric: rule.metric as TaskMetric,
    ...(prerequisiteCode !== undefined ? { prerequisiteCode: prerequisiteCode as Code | null } : {}),
  };
}

function achievementMetric(metric: string): AchievementConfig["metric"] {
  if (metric === "confirmed-taps") return "taps";
  if (metric === "confirmed-invites" || metric === "friends") return "friends";
  if (
    metric === "mechanical-level" ||
    metric === "precision-level" ||
    metric === "energy-capacity-level" ||
    metric === "workshop-stage" ||
    metric === "passive-produced" ||
    metric === "arena-matches" ||
    metric === "arena-wins" ||
    metric === "daily-streak" ||
    metric === "season-chains-completed" ||
    metric === "maxed-upgrade-branches" ||
    metric === "pvp-wins" ||
    metric === "active-friends" ||
    metric === "season-rank" ||
    metric === "season-percentile"
  ) {
    return metric;
  }
  throw new Error(`Unsupported achievement metric: ${metric}`);
}

function achievement(rule: PublishedGameConfig["publicRules"]["liveOpsCatalog"]["achievements"][number]): AchievementConfig {
  if (!ACHIEVEMENT_IDS.has(rule.id as AchievementId)) throw new Error(`Unsupported achievement: ${rule.id}`);
  if (rule.category !== "season" && rule.category !== "craft" && rule.category !== "friends") {
    throw new Error(`Unsupported achievement category: ${rule.category}`);
  }
  return {
    id: rule.id as AchievementId,
    category: rule.category,
    metric: achievementMetric(rule.metric),
    target: rule.target,
    ...(rule.comparison !== undefined ? { comparison: rule.comparison } : {}),
    ...(rule.finalSeasonOnly !== undefined ? { finalSeasonOnly: rule.finalSeasonOnly } : {}),
  };
}

function arenaEntry(entry: PublishedGameConfig["publicRules"]["contentCatalog"]["arenaCatalog"][number]): ArenaCatalogEntry {
  if (!ARENA_GAMES.has(entry.id as ArenaGameId)) throw new Error(`Unsupported Arena game: ${entry.id}`);
  if (!ARENA_AVAILABILITY.has(entry.availability as ArenaGameAvailability)) {
    throw new Error(`Unsupported Arena availability: ${entry.availability}`);
  }
  if (entry.art !== "penalty" && entry.art !== "resonance" && entry.art !== "relic") {
    throw new Error(`Unsupported Arena art: ${entry.art}`);
  }
  return {
    id: entry.id as ArenaGameId,
    availability: entry.availability as ArenaGameAvailability,
    art: entry.art,
  };
}

function premiumOffer(
  offer: PublishedGameConfig["publicRules"]["premiumCatalog"]["offers"][number],
): PremiumOfferConfig {
  if (!/^[a-z0-9][a-z0-9-]{0,63}$/.test(offer.id)) throw new Error(`Unsupported premium offer: ${offer.id}`);
  if (!PREMIUM_KINDS.has(offer.kind as PremiumOfferKind)) throw new Error(`Unsupported premium kind: ${offer.kind}`);
  if (!Number.isSafeInteger(offer.priceStars) || offer.priceStars < 1) {
    throw new Error(`Unsupported premium price: ${offer.id}`);
  }
  if (offer.purchaseLimit !== null && (!Number.isSafeInteger(offer.purchaseLimit) || offer.purchaseLimit < 1)) {
    throw new Error(`Unsupported premium purchase limit: ${offer.id}`);
  }
  if (offer.kind === "tap-production-grant") {
    if (!Number.isSafeInteger(offer.grantBasisPoints) || (offer.grantBasisPoints ?? 0) < 1) {
      throw new Error(`Unsupported tap production grant: ${offer.id}`);
    }
    if (!Number.isSafeInteger(offer.availableForSeasonSeconds) || (offer.availableForSeasonSeconds ?? 0) < 1) {
      throw new Error(`Unsupported tap production grant window: ${offer.id}`);
    }
  }
  const { collectibleId, ...base } = offer;
  if (collectibleId !== undefined && !KNOWN_COLLECTIBLE_IDS.has(collectibleId as DiamondSkinId | "season-01-signet")) {
    throw new Error(`Unsupported collectible: ${collectibleId}`);
  }
  return {
    ...base,
    id: offer.id,
    kind: offer.kind as PremiumOfferKind,
    ...(collectibleId !== undefined
      ? { collectibleId: collectibleId as DiamondSkinId | "season-01-signet" }
      : {}),
  };
}

export function applyPublishedGameConfig(value: unknown, state: GameState): void {
  assertPublishedConfig(value);
  const rules = value.publicRules;
  const season = rules.seasonRules;
  const liveOps = rules.liveOpsCatalog;
  const arena = rules.arenaRules;
  const retention = rules.rewardCampaign.retention;
  if (retention !== undefined && (!Number.isSafeInteger(retention.qualifyingReturnSeconds) ||
    retention.qualifyingReturnSeconds < 0 || retention.qualifyingReturnSeconds > 31_536_000)) {
    throw new Error("Unsupported return interval");
  }
  const upgrades = Object.fromEntries(
    [...UPGRADE_CODES].map((code) => [code, upgradeRule(season.upgrades[code])]),
  ) as Record<UpgradeCode, UpgradeTrackConfig>;
  const mechanicalLevelOne = upgrades.mechanicalCutter.levels.find((level) => level.level === 1);
  const dailyTasks = liveOps.daily.tasks.map((task) => taskRule<DailyTaskCode>(task));
  const forcedTaskCode = liveOps.daily.forcedTaskCode;
  if (forcedTaskCode !== null && !dailyTasks.some((task) => task.code === forcedTaskCode)) {
    throw new Error(`Forced daily task is missing from the published catalog: ${forcedTaskCode}`);
  }

  const next: GameConfig = {
    ...GAME_CONFIG,
    version: rules.contentCatalog.version,
    economy: {
      ...GAME_CONFIG.economy,
      initialBalance: season.economy.initialBalance,
      tapReward: season.economy.tapReward,
      mechanicalCutterCost: mechanicalLevelOne?.costToReach ?? "0",
      mechanicalCutterPassivePerHour: mechanicalLevelOne?.effectValue ?? "0",
      passiveRatePeriodSeconds: season.economy.passiveRatePeriodSeconds,
      offlineCapSeconds: season.economy.offlineCapSeconds,
      maxTapBatchSize: season.economy.maxTapBatchSize,
      maxAcceptedTapsPerSecond: season.economy.maxAcceptedTapsPerSecond,
      maxTapBurstSize: season.economy.maxTapBurstSize ?? season.economy.maxAcceptedTapsPerSecond,
    },
    energy: {
      costPerTap: season.energy.costPerTap,
      dailyRefillLimit: season.energy.dailyRefillLimit,
      refillPriceBasisPoints: season.energy.refillPriceBasisPoints,
    },
    workshop: { visualStageUpgradeThresholds: season.workshop.visualStageUpgradeThresholds },
    season: {
      ...GAME_CONFIG.season,
      initialRemainingSeconds: Math.max(0, Math.floor((Date.parse(state.season.endsAt) - Date.now()) / 1_000)),
      prizeFundUsdCents: state.season.prizeFundUsdCents,
      plannedLaunchPriceUsdtMicrosPerToken: state.season.plannedLaunchPriceUsdtMicrosPerToken,
      claimPriceStars: state.season.claimPriceStars,
      title: rules.contentCatalog.seasonTitle,
    },
    onboarding: {
      version: rules.onboardingRoute.version,
      tapTarget: rules.onboardingRoute.tapTarget,
      passiveTarget:
        rules.onboardingRoute.version === 4
          ? rules.onboardingRoute.passiveTarget
          : rules.onboardingRoute.firstOutputReward,
      firstOutputReward:
        rules.onboardingRoute.version === 4
          ? rules.onboardingRoute.passiveTarget
          : rules.onboardingRoute.firstOutputReward,
    },
    arenaCatalog: rules.contentCatalog.arenaCatalog.map(arenaEntry),
    progress: { achievements: liveOps.achievements.map(achievement) },
    liveOps: {
      daily: {
        assignmentCount: liveOps.daily.assignmentCount,
        forcedTaskCode,
        rewardBase: liveOps.daily.rewardBase,
        rewardDailyStep: liveOps.daily.rewardDailyStep,
        rewardGrowthCapDay: liveOps.daily.rewardGrowthCapDay,
        tasks: dailyTasks,
        streakMilestones: liveOps.daily.streakMilestones,
      },
      seasonTasks: liveOps.seasonTasks.map((task) => taskRule<SeasonTaskCode>(task)),
    },
    premium: {
      starsReference: rules.premiumCatalog.starsReference ?? GAME_CONFIG.premium.starsReference,
      offers: rules.premiumCatalog.offers.map(premiumOffer),
    },
    rewards: {
      ...GAME_CONFIG.rewards,
      campaignId: rules.rewardCampaign.campaignCode,
      seasonCount: rules.rewardCampaign.seasonCount,
      claimWindowSeconds: rules.rewardCampaign.claimWindowSeconds,
    },
    retention: retention === undefined ? GAME_CONFIG.retention : structuredClone(retention),
    upgrades,
    referrals: { ...season.referrals },
    profile: {
      userSeed: state.profile.userSeed,
      displayName: state.profile.displayName,
      seasonsPlayed: state.profile.seasonsPlayed,
      bestPlace: state.profile.bestPlace,
      selectedTitle: state.profile.selectedTitle,
    },
    pvp: {
      ...GAME_CONFIG.pvp,
      stake: arena.stake,
      zoneCount: arena.zoneCount,
      regulationSeries: arena.regulationSeries,
      maxSuddenDeathSeries: arena.maxSuddenDeathSeries,
      matchmakingMs: arena.guardianFallbackSeconds * 1_000,
      introMs: arena.introMilliseconds,
      turnMs: arena.turnSeconds * 1_000,
      opponentThinkMinMs: arena.guardianThinkMinMilliseconds,
      opponentThinkMaxMs: arena.guardianThinkMaxMilliseconds,
      revealMs: arena.revealMilliseconds,
      guardianName: arena.guardianName,
      guardianInitialBalance: arena.guardianInitialBalance,
    },
    friends: state.friends.map(({ id, name, line, primaryProduced }) => ({ id, name, line, primaryProduced })),
  };

  Object.assign(GAME_CONFIG as unknown as Record<string, unknown>, next);
}
